import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { distinct, filter, fromEvent, map, switchMap, tap } from 'rxjs';

import { TrackIDManager } from '@/manager';
import {
  VContextMenu,
  useFlag,
  useShortcut,
  useSubscriptions,
} from '@/package';
import {
  useBoxClipboardStore,
  useLabelingBoxStore,
  useTaskFrameStore,
  useToolMenuStore,
} from '@/store';

import { TOOL_CONTEXT_MENU_GROUP } from './constant';
import type { ToolContextMenuItem, ToolContextMenuProps } from './type';

export const ToolContextMenu = ({ parentRef }: ToolContextMenuProps) => {
  const { t } = useTranslation();
  const copiedBoxList = useBoxClipboardStore(state => state.copiedBoxList);
  const copiedTrackID = useBoxClipboardStore(state => state.copiedTrackID);
  const selectedBoxIdMap = useLabelingBoxStore(state => state.selectedBoxIdMap);
  const currentFrame = useTaskFrameStore(state => state.currentFrame);
  const toolMap = useToolMenuStore(state => state.toolMap);
  const { shortcutList, run } = useShortcut();
  const {
    value: visible,
    setFalse: hide,
    setTrue: show,
    toggle: toggleVisible,
  } = useFlag(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const selectedBoxLength = Object.keys(selectedBoxIdMap).length;
  const toolContextMenuGroupRef = useRef(
    structuredClone(TOOL_CONTEXT_MENU_GROUP),
  );
  const renderedToolContextMenuGroupList = Object.entries(
    toolContextMenuGroupRef.current,
  )
    .filter(([, value]) => {
      const visibleMenuItemList = Object.values(value).filter(
        item => !item.hidden,
      );

      return visibleMenuItemList.length > 0;
    })
    .reduce((acc, [, value]) => {
      const visibleMenuItemList = Object.values(value).filter(
        item => !item.hidden,
      );

      acc.push(visibleMenuItemList);

      return acc;
    }, [] as ToolContextMenuItem[][]);

  const findShortcutItem = useCallback(
    (command: string) => shortcutList.find(item => item.command === command),
    [],
  );

  const handleContextMenuClick = (item: ToolContextMenuItem) => {
    hide();
    run(item.command);
  };

  useSubscriptions(
    () => [
      fromEvent<PointerEvent>(window, 'pointerdown')
        .pipe(
          filter(e => {
            if (e.button !== 2 || !parentRef.current) return false;
            return parentRef.current.contains(e.target as Node);
          }),
          map(e => [e.clientX, e.clientY]),
          switchMap(([startX, startY]) => {
            let [endX, endY] = [startX, startY];

            return fromEvent<PointerEvent>(window, 'pointerup').pipe(
              tap(e => {
                [endX, endY] = [e.clientX, e.clientY];
              }),
              filter(() => endX - startX === 0 && endY - startY === 0),
              tap(() => {
                setPosition({ x: endX + 2, y: endY + 2 });
                show();
              }),
            );
          }),
        )
        .subscribe(),

      fromEvent<MouseEvent>(window, 'contextmenu')
        .pipe(
          filter(
            e =>
              parentRef.current === e.target ||
              parentRef.current?.contains(e.target as Node),
          ),
          tap(e => e.preventDefault()),
        )
        .subscribe(),
    ],
    [],
  );

  useSubscriptions(
    () => [
      fromEvent<ClipboardEvent>(navigator.clipboard, 'clipboardchange')
        .pipe(
          switchMap(() => navigator.clipboard.readText()),
          distinct(),
          tap(text => {
            // eslint-disable-next-line max-len
            toolContextMenuGroupRef.current.trackIDClipboard.pasteTrackID.disabled =
              !TrackIDManager.isValidTrackID(text);
          }),
        )
        .subscribe(),
    ],
    [],
  );

  useEffect(() => {
    const isSelectedBox = selectedBoxLength > 0;
    const isSingleSelectedBox = selectedBoxLength === 1;

    const { boxClipboard, boxNavigation, trackIDClipboard } =
      toolContextMenuGroupRef.current;

    /** hidden 설정 */
    trackIDClipboard.copyTrackID.hidden = !isSelectedBox;
    trackIDClipboard.pasteTrackID.hidden = !isSelectedBox;
    trackIDClipboard.updateTrackID.hidden = !isSelectedBox;

    boxClipboard.copyBox.hidden = !isSelectedBox;
    boxClipboard.deleteBox.hidden = !isSelectedBox;
    boxNavigation.focusPrevBox.hidden = !isSelectedBox;
    boxNavigation.focusNextBox.hidden = !isSelectedBox;

    /** disabled 설정 */
    trackIDClipboard.copyTrackID.disabled = !isSingleSelectedBox;
    trackIDClipboard.updateTrackID.disabled = !isSingleSelectedBox;
  }, [selectedBoxLength]);

  useEffect(() => {
    const { boxClipboard } = toolContextMenuGroupRef.current;

    boxClipboard.pasteBox.disabled = !copiedBoxList.length;
  }, [copiedBoxList]);

  useEffect(() => {
    const { trackIDClipboard } = toolContextMenuGroupRef.current;

    trackIDClipboard.pasteTrackID.disabled =
      !copiedTrackID || !TrackIDManager.isValidTrackID(copiedTrackID);
  }, [copiedTrackID]);

  useEffect(() => {
    hide();
  }, [currentFrame, toolMap]);

  return (
    <VContextMenu
      duration={0}
      position={position}
      visible={visible}
      onUpdateVisible={toggleVisible}
    >
      {visible &&
        renderedToolContextMenuGroupList.map((menuItemList, index) => {
          return (
            <Fragment key={index}>
              <VContextMenu.Group>
                {menuItemList.map(item => {
                  const shortcutItem = findShortcutItem(item.command);

                  if (!shortcutItem) return;

                  const shortcut = shortcutItem.fullKeyText[0];

                  return (
                    <VContextMenu.Item
                      key={item.command}
                      disabled={item.disabled}
                      shortcut={shortcut}
                      onClick={() => handleContextMenuClick(item)}
                    >
                      {shortcutItem.title
                        ? t(shortcutItem.title)
                        : shortcutItem.title}
                    </VContextMenu.Item>
                  );
                })}
              </VContextMenu.Group>

              {index !== renderedToolContextMenuGroupList.length - 1 && (
                <VContextMenu.Divider />
              )}
            </Fragment>
          );
        })}
    </VContextMenu>
  );
};
